import { useState, useEffect } from "react";
import { PaymentService, PaymentMethodVM, PlaidPaymentMethodVM } from "@propertelligent/client-api";
import PaymentAccountsTopHeader from "./PaymentAccountsTopHeader";
import { infiniteScrollHook } from "../../../hooks/infiniteScrollHook";
import PaymentAccountCardItem from "./PaymentAccountCardItem";
import { useDebounce } from "use-debounce";
import Card from "../../../newComponents/card";
import { FormProvider, useForm } from "react-hook-form";
import { PaymentMethodTypesEnum, PaymentMethodEntityTypesEnum } from "@propertelligent/client-api";
import apiHelper from "../../../utils/apiHelper";
import { UserLoginData } from "../../../components/common/charts/Enums";
import { GetImages } from "../../../utils/GetImages";
import { PlaidAccountDetails } from "../../../components/firstTimeUserExperience/Context";
import PlaidScreen from "../../../newComponents/PlaidScreen/PlaidScreen";
import PlaidPaymentAccountModals from "./PlaidPaymentAccountModals";
import EditPlaidAccountNameModal from "./EditPlaidAccountNameModal";
import { PermissionGuard } from "../../../newComponents/PermissionGuard";
import NoAccess from "../../../pages/NoAccess";
import { AccessLevelEnum } from "@propertelligent/client-api";
import { usePermissions } from "../../../hooks/usePermissions";
import {
  isAtPaymentAccountLimit,
  nextUnfilteredPaymentAccountCount,
} from "./paymentAccountLimit";

interface PaymentAccountsProps {
  className?: string;
}

// Max number of payment methods an organization can add (client-side guard; backend enforces too).
const ORG_PAYMENT_METHOD_LIMIT = 5;

const PaymentAccountsPage = ({ className = "" }: PaymentAccountsProps) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [fullyQualifiedFilter, setFullyQualifiedFilter] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState("All");
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [isReconnectModalOpen, setReconnectModalOpen] = useState(false);
  const [reconnectPaymentMethod, setReconnectPaymentMethod] = useState<PaymentMethodVM | null>(null);
  const [isRenameModalOpen, setRenameModalOpen] = useState(false);
  const [renamePaymentMethod, setRenamePaymentMethod] = useState<PaymentMethodVM | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethodVM | undefined>(undefined);
  const organizationId = Number(sessionStorage.getItem(UserLoginData.organization_id));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const check = usePermissions();
  const canWrite = check.canWrite("PaymentAccounts");

  // For Empty State
  const [hasAnyPaymentAccounts, setHasAnyPaymentAccounts] = useState<boolean>(false);
  // Hard Add-cap uses the last unfiltered total so search/pill filters cannot re-enable Add.
  const [unfilteredAccountCount, setUnfilteredAccountCount] = useState(0);

  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

  const handleFilterChange = (filter: string) => {
    updateFullyQualifiedFilter(filter, searchTerm);
  };

  const updateFullyQualifiedFilter = (filter: string, search: string) => {
    let baseFilter = "";

    switch (filter) {
      case "Plaid":
        baseFilter = `PaymentMethodTypeId == ${PaymentMethodTypesEnum.Plaid}`;
        break;
      case "All":
      default:
        baseFilter = "";
        break;
    }

    const searchFilter = search
      ? `AccountName.ToLower().Contains("${search.toLowerCase()}")`
      : "";

    const combinedFilter = [baseFilter, searchFilter].filter(Boolean).join(" &&");
    setFullyQualifiedFilter(combinedFilter || null);
  };



  const {
    rows,
    loading,
    hasMore,
    rowCount,
    appliedFilters,
    fetchData,
    currentPage,
    aggregateValues,
  } = infiniteScrollHook({
    httpFetchDataFn: PaymentService.getApiPaymentMethodOrganizationManageList,
    FullyQualifiedFilters: fullyQualifiedFilter,
    pageSize: 10,
  });

  const isAtPaymentMethodLimit = isAtPaymentAccountLimit(
    unfilteredAccountCount,
    ORG_PAYMENT_METHOD_LIMIT,
  );

  useEffect(() => {
    updateFullyQualifiedFilter(activeFilter, debouncedSearchTerm);
  }, [debouncedSearchTerm, activeFilter]);

  // infiniteScrollHook never cancels an in-flight fetch, so an older (filtered) fetch can resolve
  // -- and update rowCount -- *after* the filter has already changed again and a newer fetch has
  // been scheduled but not yet started. `appliedFilters` is the filter rowCount actually came back
  // for; only trust rowCount once it matches the currently-selected fullyQualifiedFilter, or a
  // stale filtered rowCount can get latched in as the unfiltered total. `loading` additionally
  // skips the brief window where a fetch has started but not yet resolved.
  useEffect(() => {
    if (loading) return;
    if (appliedFilters !== fullyQualifiedFilter) return;
    setUnfilteredAccountCount((previous) =>
      nextUnfilteredPaymentAccountCount(
        previous,
        rowCount,
        Boolean(fullyQualifiedFilter),
      ),
    );
  }, [rowCount, loading, appliedFilters, fullyQualifiedFilter]);

  useEffect(() => {
    if (rowCount > 0) {
      setHasAnyPaymentAccounts(true);
    }
  }, [rowCount]);



  const methods = useForm<PaymentMethodVM>({
    defaultValues: {
      id: undefined,
      accountName: "",
      paymentMethodTypeId: PaymentMethodTypesEnum.Plaid, // Plaid-only for new adds
      entityTypeId: PaymentMethodEntityTypesEnum.Organization,
      entityId: organizationId,
      achPaymentMethod: {
        accountNumber: "",
        routingNumber: "",
      },
      creditCardPaymentMethod: {
        cardHolderName: "",
        cardNumber: "",
        cvvNumber: "",
        billingZipCode: "",
        expMonth: undefined,
        expYear: undefined,
      },
    },
    mode: "onBlur",
  });


  useEffect(() => {
    if (isAddModalOpen) {
      if (selectedPaymentMethod) {
        
        methods.reset(selectedPaymentMethod);
      } else {
        methods.reset({
          id: undefined,
          accountName: "",
          paymentMethodTypeId: PaymentMethodTypesEnum.Plaid,
          entityTypeId: PaymentMethodEntityTypesEnum.Organization,
          entityId: organizationId,
          achPaymentMethod: {
            accountNumber: "",
            routingNumber: "",
          },
          creditCardPaymentMethod: {
            cardHolderName: "",
            cardNumber: "",
            cvvNumber: "",
            billingZipCode: "",
            expMonth: undefined,
            expYear: undefined,
          },
        });
      }
    }
  }, [isAddModalOpen, selectedPaymentMethod, methods, organizationId]);

  const { handleSubmit, watch, reset, formState: { isDirty } } = methods;
  const selectedPaymentMethodType = Number(watch("paymentMethodTypeId"));

  const onSubmit = async (data: PaymentMethodVM) => {

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      let payload;
      if (selectedPaymentMethodType === PaymentMethodTypesEnum.ACH) {
        payload = {
          id: data.id,
          accountName: data.accountName,
          paymentMethodTypeId: PaymentMethodTypesEnum.ACH,
          entityTypeId: PaymentMethodEntityTypesEnum.Organization,
          entityId: organizationId,
          achPaymentMethod: {
            accountNumber: data.achPaymentMethod?.accountNumber,
            routingNumber: data.achPaymentMethod?.routingNumber,
          },
          creditCardPaymentMethod: null,
        };
      } else {
        payload = {
          id: data.id,
          accountName: data.accountName,
          paymentMethodTypeId: PaymentMethodTypesEnum.CreditCard,
          entityTypeId: PaymentMethodEntityTypesEnum.Organization,
          entityId: organizationId,
          creditCardPaymentMethod: {
            cardNumber: data.creditCardPaymentMethod?.cardNumber?.split("").join(""),
            cvvNumber: data.creditCardPaymentMethod?.cvvNumber,
            billingZipCode: data.creditCardPaymentMethod?.billingZipCode,
            expMonth: data.creditCardPaymentMethod?.expMonth,
            expYear: data.creditCardPaymentMethod?.expYear,
          },
          achPaymentMethod: null,
        };
      }

      const apiCall = data.id
        ? PaymentService.putApiPaymentMethod
        : PaymentService.postApiPaymentMethod;

      const res = await apiHelper(
        apiCall,
        {
          title: data.id ? "Payment Account Updated" : "Payment Account Added",
          successMessage: data.id
            ? "Account updated successfully"
            : "Account added successfully",
          failureMessage: data.id
            ? "Unable to update payment account"
            : "Unable to add payment account",
          showNotification: true,
          successIcon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />

        },
        payload
      );

      if (res?.data?.success) {
        setAddModalOpen(false);
        reset();
        fetchData(1);
      }
    } catch (error) {
      console.error("Error submitting payment method:", error);
    }
    finally {
      setIsSubmitting(false);
    }
  };

  const handleEditPaymentMethod = (paymentMethod: PaymentMethodVM) => {
    // PaymentAccountCardItem's empty-state "Add Payment Account" button invokes this same handler with an
    // empty ({} as PaymentMethodVM) placeholder (it has no real row to edit yet) -- only treat this as
    // editing an existing method when it actually carries an id, otherwise fall through to the same
    // Plaid-only "add" flow the top-header's Add button uses so paymentMethodTypeId stays Plaid.
    if (!paymentMethod?.id) {
      setSelectedPaymentMethod(undefined);
      reset();
      setAddModalOpen(true);
      return;
    }
    setSelectedPaymentMethod(paymentMethod);
    methods.reset(paymentMethod);
    setAddModalOpen(true);
  };

  const handleModalClose = () => {
    setAddModalOpen(false);
    setSelectedPaymentMethod(undefined);
    reset({
      id: undefined,
      accountName: "",
      paymentMethodTypeId: PaymentMethodTypesEnum.Plaid,
      entityTypeId: PaymentMethodEntityTypesEnum.Organization,
      entityId: organizationId,
      achPaymentMethod: {
        accountNumber: "",
        routingNumber: "",
      },
      creditCardPaymentMethod: {
        cardNumber: "",
        cvvNumber: "",
        billingZipCode: "",
        expMonth: undefined,
        expYear: undefined,
      },
    });
  };

  // Handle Plaid account success - payment method is auto-saved by Link component
  const handlePlaidSuccess = (plaidData: PlaidAccountDetails) => {
    setAddModalOpen(false);
    setReconnectModalOpen(false);
    setReconnectPaymentMethod(null);
    reset();
    fetchData(1);
  };

  const handleReconnectPlaid = (paymentMethod: PaymentMethodVM) => {
    setReconnectPaymentMethod(paymentMethod);
    setReconnectModalOpen(true);
  };

  const handleRenamePlaidAccount = (paymentMethod: PaymentMethodVM) => {
    setRenamePaymentMethod(paymentMethod);
    setRenameModalOpen(true);
  };

  const formId = selectedPaymentMethod ? "edit-payment-method-form" : "add-payment-method-form";

  return (
    <PermissionGuard permission="PaymentAccounts" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
      <div className={`payment-accounts-page content-wrapper container-fluid ${className}`}>
        {hasAnyPaymentAccounts && (
          <PaymentAccountsTopHeader
            PageHeading="Payment Accounts"
            Count={rowCount}
            ButtonName={canWrite ? "Add Payment Account" : undefined}
            MobileButtonName={canWrite ? "Add" : undefined}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            handleFilterChange={handleFilterChange}
            aggregateValuesFilters={aggregateValues}
            isAddDisabled={isAtPaymentMethodLimit}
            addDisabledMessage={`You can add up to ${ORG_PAYMENT_METHOD_LIMIT} payment accounts.`}
            onClick={() => {
              if (isAtPaymentMethodLimit) return;
              setSelectedPaymentMethod(undefined);
              reset();
              setAddModalOpen(true);
            }}
          />
        )}

        <PaymentAccountCardItem
          dataHookState={{
            rows,
            loading,
            hasMore,
            fetchData,
            currentPage,
          }}
          onEditPaymentMethod={canWrite ? handleEditPaymentMethod : undefined}
          onReconnectPlaid={canWrite ? handleReconnectPlaid : undefined}
          onRenamePlaidAccount={canWrite ? handleRenamePlaidAccount : undefined}
          className="organisation-listing-card"
          hasAnyPaymentAccounts={hasAnyPaymentAccounts}
        />

        <PlaidPaymentAccountModals
          isAddModalOpen={isAddModalOpen}
          onAddModalClose={handleModalClose}
          isReconnectModalOpen={isReconnectModalOpen}
          reconnectPaymentMethod={reconnectPaymentMethod}
          onReconnectModalClose={() => {
            setReconnectModalOpen(false);
            setReconnectPaymentMethod(null);
          }}
          onPlaidSuccess={handlePlaidSuccess}
          plaidEntityConfig={{
            entityTypeId: PaymentMethodEntityTypesEnum.Organization,
            entityId: organizationId,
          }}
          addModalTitle={selectedPaymentMethod ? "Edit Payment Method" : "Add Payment Method"}
          addModalButtonLabel={selectedPaymentMethod ? "Update Payment Method" : "Add Payment Method"}
          addModalFormId={Number(selectedPaymentMethodType) === PaymentMethodTypesEnum.Plaid ? undefined : formId}
          isAddModalDirty={isDirty}
          isAddModalLoading={isSubmitting}
          // The Plaid Cancel+Connect footer only applies to new Plaid adds; editing a legacy ACH/CreditCard
          // row (deprecated types, still editable) falls back to RightSideModal's own buttonlabel/formId
          // footer, which submits the form below via handleSubmit(onSubmit).
          showAddModalPlaidFooter={Number(selectedPaymentMethodType) === PaymentMethodTypesEnum.Plaid}
          addModalChildren={
            <Card className="no-bg">
              <FormProvider {...methods}>
                <form id={formId} onSubmit={handleSubmit(onSubmit, (error) => {
                  console.log("Form validation errors:", error); // Log errors to console
                })}>
                  <div className="row">
                    {/* Plaid is the only supported payment method for new adds. */}
                    <div className="col-12">
                      <PlaidScreen />
                    </div>
                  </div>
                </form>
              </FormProvider>
            </Card>
          }
        />

        <EditPlaidAccountNameModal
          isOpen={isRenameModalOpen}
          paymentMethod={renamePaymentMethod}
          onClose={() => {
            setRenameModalOpen(false);
            setRenamePaymentMethod(null);
          }}
          onRenamed={() => {
            setRenameModalOpen(false);
            setRenamePaymentMethod(null);
            fetchData(1);
          }}
        />
      </div>
    </PermissionGuard>
  );
};

export default PaymentAccountsPage;